import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import TaskMatcher from "./TaskMatcher";
import HeroClock from "./HeroClock";
import { TIDS } from "@/constants/testIds";

export default function Hero({ onSlotClick }) {
  const { location } = useLocation();
  const [now, setNow] = useState(new Date());
  const sectionRef = React.useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const opacityFade = useTransform(scrollYProgress, [0, 0.85, 1], [1, 0.7, 0.25]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -160]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={sectionRef} className="relative md:min-h-screen overflow-hidden pb-10 sm:pb-12">
      {/* Corner labels */}
      <motion.div
        style={{ opacity: opacityFade }}
        className="absolute top-24 left-4 sm:left-6 md:left-10 z-10 hidden md:flex flex-col gap-3"
      >
        <span className="corner-label">ancient<br/>science</span>
        <span className="w-px h-10" style={{ background: "var(--divider)" }} />
        <span className="corner-label">modern<br/>timing</span>
      </motion.div>
      <motion.div style={{ opacity: opacityFade }} className="absolute top-24 right-4 sm:right-6 md:right-10 z-10 hidden md:flex flex-col items-end gap-2">
        <span className="corner-label">time is not clocks.</span>
        <span className="corner-label">it is <em style={{ color: "var(--accent-soft)", fontStyle: "italic" }}>rhythm.</em></span>
        <span className="section-num mt-2" style={{ color: "var(--accent-soft)" }}>★ thai gayu</span>
      </motion.div>

      {/* Top brand */}
      <motion.div
        style={{ y: titleY, opacity: opacityFade }}
        className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 md:px-10 pt-20 sm:pt-22 md:pt-24 text-center"
      >
        <motion.img
          src="/thaigayu-logo-gold.png"
          alt="thai gayu"
          initial={{ y: 40, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.15, ease: [0.7, 0, 0.2, 1] }}
          data-testid={TIDS.heroTitle}
          className="mx-auto w-[min(54vw,260px)] sm:w-[min(34vw,280px)] md:w-[min(20vw,280px)] h-auto select-none"
          style={{
            mixBlendMode: "lighten",
            filter:
              "drop-shadow(0 16px 38px rgba(0,0,0,0.55)) drop-shadow(0 0 30px rgba(184,155,94,0.35))",
          }}
          draggable={false}
        />

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.85 }}
          data-testid={TIDS.heroSubtitle}
          className="font-display text-base sm:text-lg md:text-xl max-w-[92vw] sm:max-w-xl md:max-w-2xl mx-auto leading-snug mt-1.5 sm:mt-3 px-3"
          style={{ color: "var(--ink-muted)" }}
        >
          Thai gayu means &lsquo;<em style={{ color: "var(--accent-soft)" }}>it is done</em>.&rsquo;<br/>
          Tell us what. We&apos;ll tell you when.
        </motion.p>
      </motion.div>

      {/* THE CLOCK */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 1.0, ease: [0.7, 0, 0.2, 1] }}
        className="relative z-10 mt-5 sm:mt-7"
        data-testid={TIDS.heroCurrentChoghadiya}
      >
        <HeroClock location={location} now={now} onSlotClick={onSlotClick} />
      </motion.div>

      {/* TASK MATCHER — the headline action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 1.2, ease: [0.7, 0, 0.2, 1] }}
        style={{ opacity: opacityFade }}
        className="relative z-10 mt-7 sm:mt-9"
      >
        <TaskMatcher onSlotClick={onSlotClick} />
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.75 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="relative z-10 mt-7 sm:mt-9 flex flex-col items-center gap-1.5"
      >
        <span className="corner-label">scroll for the day&apos;s windows</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={14} style={{ color: "var(--ink-muted)" }} />
        </motion.div>
      </motion.div>
    </section>
  );
}
